import { useState, useCallback, useEffect } from 'react';
import { getEffectiveParentId } from '../utils/orphanDetection';

// LocalStorage keys
const EXPANDED_SPRINTS_KEY = 'sidebar-expanded-sprints';
const CAMPAIGNS_EXPANDED_KEY = 'sidebar-campaigns-expanded';
const TEMPLATES_EXPANDED_KEY = 'sidebar-templates-expanded';
const VIEWS_EXPANDED_KEY = 'sidebar-views-expanded';

/**
 * Get stored expanded sprints from localStorage
 */
const getStoredExpandedSprints = () => {
  try {
    const stored = localStorage.getItem(EXPANDED_SPRINTS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * Save expanded sprints to localStorage
 */
const saveExpandedSprints = (expandedSet) => {
  try {
    localStorage.setItem(EXPANDED_SPRINTS_KEY, JSON.stringify([...expandedSet]));
  } catch (e) {
    console.warn('Failed to save expanded sprints:', e);
  }
};

/**
 * Get stored section expansion state from localStorage
 */
const getStoredSectionState = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Save section expansion state to localStorage
 */
const saveSectionState = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Failed to save section state:', e);
  }
};

/**
 * Hook for managing sidebar section expansion states
 * Persists state to localStorage
 * 
 * @param {Array} sprints - All sprints/campaigns
 * @param {Function} onSelectSprint - Callback when a sprint is selected
 * @param {string} selectedSprintId - Currently selected sprint ID (for auto-expand)
 */
export function useSectionState(sprints, onSelectSprint, selectedSprintId) {
  // Section expansion states - persisted to localStorage
  const [campaignsExpanded, setCampaignsExpanded] = useState(() =>
    getStoredSectionState(CAMPAIGNS_EXPANDED_KEY, true)
  );
  const [templatesExpanded, setTemplatesExpanded] = useState(() =>
    getStoredSectionState(TEMPLATES_EXPANDED_KEY, false)
  );
  const [viewsExpanded, setViewsExpanded] = useState(() =>
    getStoredSectionState(VIEWS_EXPANDED_KEY, true)
  );
  const [expandedSprints, setExpandedSprints] = useState(() =>
    getStoredExpandedSprints()
  );

  // Auto-expand parent campaigns so the selected campaign is visible in the sidebar
  // (Does NOT expand the selected campaign itself - just reveals it by expanding ancestors)
  useEffect(() => {
    if (!selectedSprintId) return;
    
    const selectedSprint = sprints.find(s => s.id === selectedSprintId);
    if (!selectedSprint) return;
    
    setExpandedSprints(prev => {
      const next = new Set(prev);
      let needsUpdate = false;
      
      // Expand all parent campaigns up the hierarchy (task links count too)
      let parentId = getEffectiveParentId(selectedSprint, sprints);
      while (parentId) {
        if (!next.has(parentId)) {
          next.add(parentId);
          needsUpdate = true;
        }
        const parent = sprints.find(s => s.id === parentId);
        parentId = parent ? getEffectiveParentId(parent, sprints) : null;
      }
      
      if (needsUpdate) {
        saveExpandedSprints(next);
        return next;
      }
      return prev;
    });
  }, [selectedSprintId, sprints]);

  // Toggle expand/collapse for nested sprints - persisted to localStorage
  const handleToggleExpand = useCallback((sprintId) => {
    setExpandedSprints(prev => {
      const next = new Set(prev);
      if (next.has(sprintId)) {
        next.delete(sprintId);
      } else {
        next.add(sprintId);
      }
      saveExpandedSprints(next);
      return next;
    });
  }, []);

  /** Collapse all nested campaign/template trees (same Set as Explorer "collapse folders"). */
  const handleCollapseAllCampaignTrees = useCallback(() => {
    const next = new Set();
    saveExpandedSprints(next);
    setExpandedSprints(next);
  }, []);

  // Auto-expand parent when selecting nested sprint
  // Supports options object for view and mission focus
  const handleSelectSprint = useCallback((sprint, options = {}) => {
    const firstParentId = getEffectiveParentId(sprint, sprints);
    if (firstParentId) {
      setExpandedSprints(prev => {
        const next = new Set(prev);
        let parentId = firstParentId;
        while (parentId) {
          next.add(parentId);
          const parent = sprints.find(s => s.id === parentId);
          parentId = parent ? getEffectiveParentId(parent, sprints) : null;
        }
        saveExpandedSprints(next);
        return next;
      });
    }
    onSelectSprint?.(sprint, options);
  }, [sprints, onSelectSprint]);

  // Toggle campaigns section - persisted to localStorage
  const handleToggleCampaigns = useCallback(() => {
    setCampaignsExpanded(prev => {
      const next = !prev;
      saveSectionState(CAMPAIGNS_EXPANDED_KEY, next);
      return next;
    });
  }, []);

  // Toggle templates section - persisted to localStorage
  const handleToggleTemplates = useCallback(() => {
    setTemplatesExpanded(prev => {
      const next = !prev;
      saveSectionState(TEMPLATES_EXPANDED_KEY, next);
      return next;
    });
  }, []);

  // Toggle views section - persisted to localStorage
  const handleToggleViews = useCallback(() => {
    setViewsExpanded(prev => {
      const next = !prev;
      saveSectionState(VIEWS_EXPANDED_KEY, next);
      return next;
    });
  }, []);

  return {
    campaignsExpanded,
    templatesExpanded,
    viewsExpanded,
    expandedSprints,
    handleToggleExpand,
    handleCollapseAllCampaignTrees,
    handleSelectSprint,
    handleToggleCampaigns,
    handleToggleTemplates,
    handleToggleViews,
  };
}

export default useSectionState;

